import { Clear } from "@mui/icons-material";
import { Grid, IconButton, styled } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { uploadImage } from "utils/networkUtils";
import { ImageDialog } from "./ImageDialog";

const MDTypography = require("components/MDTypography/index.js").default;
const MDButton = require("components/MDButton/index.js").default;
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: "100%",
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: "100%",
});

let urls: string[] = [];
export const ImageUploader = ({
  initImage,
  onUploaded,
  count = 1,
  folder,
  buttonOnly,
  buttonTitle,
  fileType = "image/*",
  iconOnly,
}: {
  initImage?: string[];
  onUploaded: (images: string[]) => void;
  count?: number;
  folder: string;
  buttonOnly?: boolean;
  buttonTitle?: string;
  fileType?: string;
  iconOnly?: boolean;
}) => {
  const [images, setImages] = useState(initImage || []);

  const onPaste = (event) => {
    const clipboardData = event.clipboardData;
    if (clipboardData) {
      const items = Array.from(clipboardData.items) as DataTransferItem[];
      const fileItem = items.find((item) => item.kind === "file");

      if (fileItem) {
        console.log("Found file in clipboard");

        const blob = fileItem.getAsFile();
        if (!blob) return;

        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const res = await uploadImage(blob, folder);
            setImages(
              (prevImages) =>
                [
                  ...prevImages.slice(0, count - 1),
                  reader.result, // Updated with pasted image preview
                ] as string[]
            );

            urls = [
              ...urls.slice(0, count - 1),
              res.Location, // Uploaded image URL
            ];

            onUploaded(urls); // Ensure this gets the latest URLs
          } catch (error) {
            console.error("Error uploading image:", error);
          }
        };
        reader.readAsDataURL(blob);
      }
    }
  };

  useEffect(() => {
    document.addEventListener("paste", onPaste);
    return () => {
      document.removeEventListener("paste", onPaste);
    };
  }, []);

  useEffect(() => {
    setImages(initImage || []);
    urls = [];
  }, [initImage]);

  const pickImage = async (e) => {
    if (!e.target.files) return;
    const previews: string[] = [];
    Promise.all(
      (Array.from(e.target.files) as File[]).slice(0, count).map(
        (file) =>
          new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              previews.push(reader.result as string);
              uploadImage(file, folder).then((res) => resolve(res.Location));
            };
            reader.readAsDataURL(file);
          })
      )
    ).then((res: string[]) => {
      setImages(images.slice(0, count - previews.length).concat(previews));
      urls = urls.slice(0, count - previews.length).concat(res);
      onUploaded(urls);
    });
  };

  return iconOnly ? (
    <MDButton component="label" variant="text" tabIndex={-1} startIcon={<CloudUploadIcon />} color="success">
      <VisuallyHiddenInput
        type="file"
        onChange={pickImage}
        accept={fileType}
        multiple={count > 1}
      />
    </MDButton>
  ) : buttonOnly ? (
    <MDButton component="label" variant="outlined" color="primary">
      <VisuallyHiddenInput
        type="file"
        onChange={pickImage}
        accept={fileType}
        multiple={count > 1}
      />
      {buttonTitle || "Browse"}
    </MDButton>
  ) : (
    <Grid
      container
      alignItems={"center"}
      justifyContent={"space-between"}
      style={{
        border: "1px solid black",
        borderRadius: "5px",
        padding: "5px 15px",
      }}
    >
      {!!images.length ? (
        images.map((image) => <ImageDialog url={image} size="100px" />)
      ) : (
        <MDTypography variant="body2">
          Upload Image{count > 1 ? `s (up to ${count})` : ""}
        </MDTypography>
      )}

      {!!images.length && (
        <IconButton
          onClick={() => {
            setImages([]);
            onUploaded([]);
          }}
        >
          <Clear />
        </IconButton>
      )}
      <MDButton component="label" variant="outlined" color="primary">
        <VisuallyHiddenInput
          type="file"
          onChange={pickImage}
          accept={fileType}
          multiple={count > 1}
        />
        {buttonTitle || "Browse"}
      </MDButton>
    </Grid>
  );
};
